import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/use-auth";
import { apiRequest } from "@/lib/queryClient";
import { useTaskProjects } from "@/hooks/use-tasks";
import { useWikiTree } from "@/hooks/use-wiki";
import RunningTimerChip from "@/components/tasks/running-timer-chip";
import { useLocation } from "wouter";
import { Link } from "wouter";
import {
  Bell,
  ChevronDown,
  ChevronRight,
  LogOut,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";

/** `/tasks/12` — the one route whose title isn't a constant. */
const TASK_BOARD_ROUTE = /^\/tasks\/(\d+)$/;
// A client's Universe carries the client's name in the page itself, so the
// header just names the surface. Without an entry here every Universe route
// falls through PAGE_TITLES and reads "Sidekick".
const UNIVERSE_ROUTE = /^\/clients\/universe\/([^/]+)/;
// `/clients/3` — the client's name lives in the page's own title block, so the
// header names the surface and lets the breadcrumb stay generic. Declared after
// UNIVERSE_ROUTE and tested after it, since /clients/universe/:uid would
// otherwise match this too.
const CLIENT_DETAIL_ROUTE = /^\/clients\/[^/]+$/;
// `/clients/ducks/<uid>`, named for the nav item it sits under.
const CLIENT_DUCKS_ROUTE = /^\/clients\/ducks\/[^/]+$/;

const PAGE_TITLES: Record<string, string> = {
  "/":                     "Dashboard",
  "/clients":              "Clients",
  "/ducks":                "Ducks in a Row",
  "/payments":             "Payments",
  "/overtime-requests":    "Overtime",
  "/missions":             "Epic Missions",
  "/legal-documents":      "Legal Documents",
  "/referral-leaderboard": "Leaderboard",
  "/community":            "Community",
  "/exemplar":             "Exemplar Profile",
  "/settings":             "Settings",
  "/tasks":                "Tasks",
};

export default function Header() {
  const { user, logoutMutation } = useAuth();
  const [location] = useLocation();
  const [open, setOpen] = useState(false);

  // A project board is "Tasks › <board>", not a title of its own. Before this,
  // /tasks/12 matched nothing in PAGE_TITLES and every board read "Sidekick".
  const boardMatch = TASK_BOARD_ROUTE.exec(location);
  // Subscribed only on that route — the header is mounted on every page, and an
  // unconditional call would fetch the whole roster behind Dashboard and
  // Billing. On the board itself the page has already populated this cache.
  const { data: projectsData } = useTaskProjects(!!boardMatch);
  const boardName = boardMatch
    ? projectsData?.projects.find((p) => p.id === Number(boardMatch[1]))?.name
    : undefined;

  // A Universe is "Clients › <client> · Universe". Same trick as the board:
  // the query is keyed and enabled exactly as the page's own is, so on the
  // Universe route this reads a cache the page has already filled, and
  // everywhere else it never fires.
  const universeMatch = UNIVERSE_ROUTE.exec(location);
  const { data: wikiTree } = useWikiTree(universeMatch?.[1]);
  const universeLeaf = universeMatch
    ? `${wikiTree?.client_name ?? "Client"} · Universe`
    : undefined;

  const pageTitle = boardMatch
    ? "Tasks"
    : universeMatch
      ? "Clients"
      : CLIENT_DUCKS_ROUTE.test(location)
        ? "Ducks in a Row"
        : CLIENT_DETAIL_ROUTE.test(location)
          ? "Clients"
          : (PAGE_TITLES[location] ?? "Sidekick");

  const crumbLeaf = boardName ?? universeLeaf;

  // Avatar initials from name
  const initials = (user?.name ?? "U")
    .split(" ")
    .map((w: string) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const profileQuery = useQuery({
    queryKey: ["sidekick_profile"],
    staleTime: 45 * 60 * 1000,
    queryFn: async () => {
      try {
        const res = await apiRequest("GET", "/sidekick_profile/me");
        return res.json();
      } catch (err) {
        if (err instanceof Error && err.message.startsWith("404:")) return null;
        throw err;
      }
    },
  });

  const profilePhotoUrl = profileQuery.data?.profile_picture_url ?? null;

  return (
    <header className="border-b border-border-subtle bg-card px-4 lg:px-5 h-[58px] lg:h-[52px] flex items-center justify-between flex-shrink-0">
      {/* Left: page title, or a breadcrumb when the page is inside something */}
      <div className="flex min-w-0 items-center gap-2.5">
        <h2 className="flex-shrink-0 text-[19px] font-extrabold tracking-[-0.02em] text-foreground lg:text-sm lg:font-bold lg:tracking-[-0.01em] lg:text-slate-800 lg:dark:text-foreground">
          {pageTitle}
        </h2>
        {crumbLeaf && (
          <span className="hidden min-w-0 items-center gap-2.5 lg:flex">
            <ChevronRight className="h-3.5 w-3.5 flex-shrink-0 text-slate-300 dark:text-slate-600" />
            <span className="truncate text-sm font-medium text-muted-foreground">
              {crumbLeaf}
            </span>
          </span>
        )}
      </div>

      {/* Right: running timer + bell + user */}
      <div className="flex items-center gap-2 lg:gap-1">
        <RunningTimerChip />
        {/* A rule between the timer and the account controls: without it the
            green pill read as part of the same cluster as the bell. */}
        <span className="hidden h-5 w-px bg-border lg:block" />
        {/* Bell — round w/ rose notification dot on mobile */}
        <button
          type="button"
          className="relative flex h-[38px] w-[38px] items-center justify-center rounded-full bg-slate-500/10 text-muted-foreground hover:bg-slate-500/15 lg:h-9 lg:w-9 lg:bg-transparent lg:text-slate-400 lg:hover:bg-slate-100 lg:hover:text-slate-700 dark:lg:hover:bg-slate-800"
        >
          <Bell style={{ width: 18, height: 18 }} />
          <span className="absolute right-[10px] top-[9px] h-[7px] w-[7px] rounded-full bg-rose-500 ring-[1.5px] ring-card" />
          <span className="sr-only">Notifications</span>
        </button>

        <DropdownMenu open={open} onOpenChange={setOpen}>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className="flex items-center gap-2 px-0 lg:px-2 h-auto lg:h-9 rounded-full lg:rounded-lg text-sm hover:bg-transparent lg:hover:bg-slate-100 dark:lg:hover:bg-slate-800 focus-visible:ring-0"
            >
              <span className="h-9 w-9 lg:h-[26px] lg:w-[26px] rounded-full bg-blue-600 flex items-center justify-center text-white text-xs lg:text-[10px] font-bold flex-shrink-0 overflow-hidden">
                {profilePhotoUrl ? (
                  <img src={profilePhotoUrl} alt={user?.name ?? "Profile"} className="h-full w-full object-cover" />
                ) : (
                  initials
                )}
              </span>
              <span className="text-sm font-medium text-slate-700 dark:text-foreground hidden lg:block leading-none">
                {user?.name || "User"}
              </span>
              <ChevronDown className="text-slate-400 hidden lg:block h-3.5 w-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            <DropdownMenuLabel className="text-xs text-slate-500 font-normal">
              {user?.email || "My Account"}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <Link href="/settings">
              <DropdownMenuItem onSelect={(e) => e.preventDefault()} className="cursor-pointer text-sm">
                Settings
              </DropdownMenuItem>
            </Link>
            <DropdownMenuItem
              onSelect={(e) => { e.preventDefault(); logoutMutation.mutate(); }}
              className="cursor-pointer text-red-600 text-sm"
            >
              <LogOut className="h-3.5 w-3.5 mr-2" />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
